<script>
import { permissionsService } from '@utils/permissions'
import { httpServices } from '@utils/http-services'

export default {
  props: {
    containers: {
      type: Array,
      required: true,
    },
    groups: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      searchText: '',
      searchInput: null,
      searchedColumn: '',
      isVanishedAdmin: permissionsService.isVanishedAdmin(),
      columns: [
        {
          title: 'ID',
          dataIndex: 'bonfireId',
          key: 'bonfireId',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.bonfireId.localeCompare(b.bonfireId),
        },
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
          title: 'Hardware',
          dataIndex: 'hardwareId',
          key: 'hardwareId',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.hardwareId.localeCompare(b.hardwareId),
          // specify the condition of filtering result
          // here is that finding the name started with `value`
          onFilter: (value, record) => record.hardwareId.indexOf(value) === 0,
        },
        {
          title: 'Active User(s)',
          dataIndex: 'currentUsers',
          key: 'currentUsers',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.currentUsers - b.currentUsers,
        },
        {
          title: 'Group(s)',
          dataIndex: 'groupId',
          key: 'groupId',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
        {
          title: 'Organization',
          dataIndex: 'orgId',
          key: 'orgId',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.orgId.localeCompare(b.orgId),
        },
        {
          title: 'State',
          dataIndex: 'state',
          key: 'state',
          scopedSlots: {
            customRender: 'customRender',
          },
          defaultSortOrder: 'descend',
          sorter: (a, b) => a.state?.localeCompare(b?.state),
        },
        {
          title: 'Actions',
          key: 'actions',
          scopedSlots: {
            customRender: 'customRender',
          },
        },
      ],
    }
  },
  methods: {
    getGroupColor(id) {
      if (id && this.groups) {
        const group = this?.groups.filter((g) => g.sk === id)[0]
        const color = group?.meta?.color
        if (color) {
          return color
        } else {
          return 'pink'
        }
      } else {
        return 'pink'
      }
    },
    selectRow(record) {
      const containerId = record.pk.replace('CONTAINER#', '')
      this.$router.push({
        name: 'container-profile',
        params: { containerId },
      })
    },
    goToContainer(id) {
      const groupId = id.replace('GROUP#', '')
      this.$router.push({
        name: 'group-profile',
        params: { groupId },
      })
    },
    onDelete(record) {
      this.$emit('deleteContainer', record)
    },
    onStart(record) {
      httpServices.startFleet(record).then(() => this.$router.go(0))
    },
    onStop(record) {
      httpServices.stopFleet(record).then(() => this.$router.go(0))
    },
    handleSearch(selectedKeys, confirm, dataIndex) {
      confirm()
      this.searchText = selectedKeys[0]
      this.searchedColumn = dataIndex
    },
    sortGroup(record) {
      if (record?.groupId?.length > 0) {
        const x = JSON.parse(JSON.stringify(record))
        return x.groupId.sort()
      }
      return []
    },
    handleReset(clearFilters) {
      clearFilters()
      this.searchText = ''
    },
  },
}
</script>

<template>
  <a-table :data-source="containers" :columns="columns">
    <div
      slot="filterDropdown"
      slot-scope="{
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        column,
      }"
      style="padding: 8px"
    >
      <a-input
        v-ant-ref="(c) => (searchInput = c)"
        :placeholder="`Search ${column.dataIndex}`"
        :value="selectedKeys[0]"
        style="display: block; width: 188px; margin-bottom: 8px"
        @change="(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])"
        @pressEnter="
          () => handleSearch(selectedKeys, confirm, column.dataIndex)
        "
      />
      <a-button
        type="primary"
        icon="search"
        size="small"
        style="width: 90px; margin-right: 8px"
        @click="() => handleSearch(selectedKeys, confirm, column.dataIndex)"
      >
        Search
      </a-button>
      <a-button
        size="small"
        style="width: 90px"
        @click="() => handleReset(clearFilters)"
      >
        Reset
      </a-button>
    </div>
    <a-icon
      slot="filterIcon"
      slot-scope="filtered"
      type="search"
      :style="{ color: filtered ? '#108ee9' : undefined }"
    />
    <template slot="customRender" slot-scope="text, record, index, column">
      <span v-if="searchText && searchedColumn === column.dataIndex">
        <template
          v-for="(fragment, i) in text
            .toString()
            .split(new RegExp(`(?<=${searchText})|(?=${searchText})`, 'i'))"
        >
          <mark
            v-if="fragment.toLowerCase() === searchText.toLowerCase()"
            :key="i"
            class="highlight"
            >{{ fragment }}</mark
          >
          <template v-else>{{ fragment }}</template>
        </template>
      </span>
      <template v-else>
        <div v-if="column.key === 'bonfireId'">
          <a @click="() => selectRow(record, column)">{{ text }}</a>
        </div>
        <div v-else-if="column.key === 'groupId'">
          <div v-if="record.groupId.length">
            <a-tag
              v-for="(id, i) in sortGroup(record)"
              :key="i"
              style="text-transform: uppercase"
              :color="getGroupColor(id)"
              class="group--tag"
              @click="goToContainer(id)"
              >{{ id.replace('GROUP#', '') }}
            </a-tag>
          </div>
        </div>
        <div v-else-if="column.key === 'orgId'">
          <a-tag color="green">
            {{ text.toUpperCase() }}
          </a-tag>
        </div>
        <div v-else-if="column.key === 'currentUsers'">
          {{ text }}
        </div>
        <div v-else-if="column.key === 'state'">
          <a-tag v-if="text === 'STOPPED' || text === 'STOPPING'" color="red">
            {{ text }}
          </a-tag>
          <a-tag v-else color="green"> {{ text }}</a-tag>
        </div>
        <div v-else-if="column.key === 'actions'">
          <ul class="actions">
            <li>
              <a
                href="javascript:;"
                title="Edit Container"
                @click="() => selectRow(record, column)"
              >
                <a-button type="primary" icon="edit" size="small"></a-button>
              </a>
            </li>
            <li>
              <a-popconfirm
                title="Sure to delete?"
                @confirm="() => onDelete(record)"
              >
                <a href="javascript:;" title="Delete Container">
                  <a-button type="danger" icon="delete" size="small"></a-button>
                </a>
              </a-popconfirm>
            </li>
            <li
              v-if="
                isVanishedAdmin &&
                (record.state === 'RUNNING' || record.state === 'STARTING')
              "
            >
              <a-popconfirm
                title="Do you want to stop container?"
                @confirm="() => onStop(record)"
              >
                <a href="javascript:;" title="Stop Container">
                  <a-button
                    type="danger"
                    icon="pause-circle"
                    size="small"
                  ></a-button>
                </a>
              </a-popconfirm>
            </li>
            <li
              v-if="
                isVanishedAdmin &&
                (record.state === 'STOPPED' || record.state === 'STOPPING')
              "
            >
              <a-popconfirm
                title="Do you want to start container?"
                @confirm="() => onStart(record)"
              >
                <a href="javascript:;" title="Start Container">
                  <a-button
                    type="primary"
                    icon="play-circle"
                    size="small"
                  ></a-button>
                </a>
              </a-popconfirm>
            </li>
          </ul>
        </div>
        <div v-else>
          {{ text.toUpperCase() }}
        </div>
      </template>
    </template>
  </a-table>
</template>

<style scoped>
.highlight {
  padding: 0;
  background-color: rgb(255, 192, 105);
}
</style>
